import { User } from "../types";
import { API_BASE_URL } from "./apiBaseUrl";
import { getMessengerInitData, getOrdersMessengerPayload } from "@/shared/utils/runtimeMessenger";

class UserApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  async getUserByMessenger(
    messenger: "telegram" | "max",
    telegramId?: number,
    userId?: number
  ): Promise<User | null> {
    const params = new URLSearchParams();

    params.append("messenger", messenger);

    const initData = getMessengerInitData();
    if (initData) {
      params.append("init_data", initData);
    }

    if (messenger === "telegram") {
      if (telegramId == null) {
        console.error("Invalid parameters for getUserByMessenger: telegram id");
        return null;
      }
      params.append("telegram_id", String(telegramId));
    } else if (messenger === "max") {
      if (userId == null) {
        console.error("Invalid parameters for getUserByMessenger: max user id");
        return null;
      }
      params.append("user_id", String(userId));
    } else {
      console.error("Invalid parameters for getUserByMessenger");
      return null;
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/GetUser.php?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        return data.user;
      } else {
        console.warn("User not found:", data.error);
        return null;
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      return null;
    }
  }

  async createOrUpdateUser(userData: {
    messenger_type: "telegram" | "max";
    messenger_id?: string;
    max_id?: string;
    first_name?: string;
    last_name?: string;
    username?: string;
    language_code?: string;
    phone?: string;
  }): Promise<User | null> {
    try {
      const { init_data: initData } = getOrdersMessengerPayload();
      const response = await fetch(`${this.baseUrl}/CreateOrUpdateUser.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...userData, init_data: initData }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        return data.user;
      } else {
        console.error("Failed to create/update user:", data.error);
        return null;
      }
    } catch (error) {
      console.error("Error creating/updating user:", error);
      return null;
    }
  }

  async updateUser(
    messengerId: string,
    data: {
      phone?: string;
      first_name?: string;
      last_name?: string;
    }
  ): Promise<User | null> {
    try {
      const response = await fetch(`${this.baseUrl}/UpdateUser.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          messenger_id: messengerId,
          ...data,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return result.user;
      } else {
        console.error("Failed to update user:", result.error);
        return null;
      }
    } catch (error) {
      console.error("Error updating user:", error);
      return null;
    }
  }
}

export const userApi = new UserApi();
